import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, MessageSquare, Wrench, Bot, BarChart3, Shield,
  Zap, CheckCircle2, Clock, DollarSign, Users, TrendingUp,
  Star, ArrowRight, Mail, Phone, MapPin, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Landing() {
  const navigate = useNavigate();
  const [activePlan, setActivePlan] = useState('professional');

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Bar */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-lg border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                PropManager AI
              </span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">Features</a>
              <a href="#how-it-works" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">How It Works</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">Pricing</a>
              <a href="#testimonials" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">Reviews</a>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/login')} className="text-gray-700">
                Sign In
              </Button>
              <Button
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg"
              >
                Start Free Trial
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50 -z-10" />
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                <span>AI-Powered Property Management</span>
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
                Manage Properties
                <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  10x Faster
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                The first AI-native property management platform built for Australian agencies.
                Automate maintenance, triage messages, and delight tenants—all in one intelligent system.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Button
                  size="lg"
                  onClick={() => navigate('/login')}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all"
                >
                  Start Free 14-Day Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                  className="px-8 py-6 text-lg border-2 border-gray-300 hover:border-gray-400"
                >
                  See Features
                </Button>
              </div>
              <div className="flex items-center gap-8 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>Cancel anytime</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-8 border-white bg-white">
                {/* Mock Dashboard Preview */}
                <div className="aspect-video bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
                  {/* Mock header */}
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg"></div>
                      <span className="font-bold text-gray-800">PropManager AI</span>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-20 h-8 bg-white rounded border"></div>
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full"></div>
                    </div>
                  </div>
                  {/* Mock stats */}
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                        <div className="w-8 h-8 bg-blue-100 rounded mb-2"></div>
                        <div className="h-2 bg-gray-200 rounded w-3/4 mb-1"></div>
                        <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                      </div>
                    ))}
                  </div>
                  {/* Mock job cards */}
                  <div className="grid grid-cols-2 gap-3">
                    {[1, 2].map(i => (
                      <div key={i} className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-2">
                          <Wrench className="w-4 h-4 text-orange-500" />
                          <div className="h-3 bg-gray-300 rounded w-2/3"></div>
                        </div>
                        <div className="h-2 bg-gray-200 rounded w-full mb-1"></div>
                        <div className="h-2 bg-gray-200 rounded w-4/5"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Time Saved</p>
                    <p className="text-2xl font-bold text-gray-900">15hrs/week</p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 bg-white rounded-xl shadow-xl p-4 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Clock className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Response Time</p>
                    <p className="text-2xl font-bold text-gray-900">&lt;2min</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-12 px-6 bg-gray-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-gray-600 mb-8 text-sm uppercase tracking-wide font-medium">
            Trusted by Australian Property Managers
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center opacity-60">
            {['Sydney Metro', 'Melbourne CBD', 'Brisbane North', 'Perth West'].map((city) => (
              <div key={city} className="text-center text-gray-400 font-semibold text-lg">
                {city}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Why PropManager Beats Traditional Software
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stop juggling spreadsheets, emails, and outdated systems. PropManager's AI does the heavy lifting.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-white border border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-red-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Clock className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Old Way</h3>
              <p className="text-gray-600 mb-4">Manual job logging, endless phone calls, lost emails, tenant complaints</p>
              <div className="inline-flex items-center gap-2 text-red-600 font-medium">
                <span>15+ hours/week wasted</span>
              </div>
            </div>
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 shadow-xl relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                PropManager Way
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Bot className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">AI-Powered</h3>
              <p className="text-gray-600 mb-4">Automatic triage, instant tenant responses, smart tradie dispatch, full timeline</p>
              <div className="inline-flex items-center gap-2 text-green-600 font-bold text-lg">
                <TrendingUp className="w-5 h-5" />
                <span>80% time saved</span>
              </div>
            </div>
            <div className="text-center p-8 rounded-2xl bg-white border border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-orange-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <DollarSign className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">ROI</h3>
              <p className="text-gray-600 mb-4">Manage 3x more properties with the same team. Scale without hiring.</p>
              <div className="inline-flex items-center gap-2 text-green-600 font-medium">
                <span>Pay for itself in Week 1</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Everything You Need, Nothing You Don't
            </h2>
            <p className="text-xl text-gray-600">
              Purpose-built for Australian property managers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: <Bot className="w-7 h-7" />,
                title: 'AI Maintenance Triage',
                description: 'Automatically classify jobs, suggest urgency, and assign tradies—no manual work',
                color: 'from-purple-500 to-purple-600'
              },
              {
                icon: <Wrench className="w-7 h-7" />,
                title: 'Smart Job Dispatch',
                description: 'Tenants upload photos, AI classifies trade type, PM assigns with one click',
                color: 'from-blue-500 to-blue-600'
              },
              {
                icon: <MessageSquare className="w-7 h-7" />,
                title: 'Unified Inbox',
                description: 'Email, SMS, portal messages—all in one place with AI summaries',
                color: 'from-green-500 to-green-600'
              },
              {
                icon: <BarChart3 className="w-7 h-7" />,
                title: 'Real-Time Dashboard',
                description: 'Track arrears, maintenance, lease renewals at a glance',
                color: 'from-orange-500 to-orange-600'
              },
              {
                icon: <Building2 className="w-7 h-7" />,
                title: 'Property & Lease Management',
                description: 'Digital leases, automated reminders, tenant portals included',
                color: 'from-indigo-500 to-indigo-600'
              },
              {
                icon: <Shield className="w-7 h-7" />,
                title: 'Role-Based Access',
                description: 'Agents, landlords, tenants—everyone sees only what they should',
                color: 'from-red-500 to-red-600'
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="p-6 rounded-2xl bg-white border border-gray-200 hover:border-gray-300 hover:shadow-xl transition-all group"
              >
                <div className={`w-14 h-14 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">
              From maintenance request to resolution in 3 simple steps
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Tenant Reports Issue',
                description: 'Tenant logs maintenance job via portal with photos. Receives instant confirmation email.',
                icon: <Wrench className="w-8 h-8" />
              },
              {
                step: '02',
                title: 'AI Classifies & Alerts PM',
                description: 'AI identifies trade type (plumber, sparky, etc.) and urgency. PM gets smart notification.',
                icon: <Bot className="w-8 h-8" />
              },
              {
                step: '03',
                title: 'PM Assigns & Tracks',
                description: 'PM assigns tradie in seconds. Tenant gets updates. Full timeline tracked automatically.',
                icon: <CheckCircle2 className="w-8 h-8" />
              }
            ].map((item, index) => (
              <div key={index} className="relative">
                {index < 2 && (
                  <div className="hidden md:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 -z-10" />
                )}
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full text-white text-2xl font-bold mb-6 shadow-xl">
                    {item.icon}
                  </div>
                  <div className="text-blue-600 font-bold text-sm mb-2">STEP {item.step}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Loved by Property Managers
            </h2>
            <p className="text-xl text-gray-600">
              See why agencies are switching to PropManager
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah Mitchell',
                role: 'Senior Property Manager',
                company: 'Sydney Metro Realty',
                quote: 'PropManager cut our maintenance response time from 2 days to 2 hours. Tenants are happier, landlords are happier. Game changer.',
                rating: 5
              },
              {
                name: 'James Wong',
                role: 'Agency Principal',
                company: 'Melbourne Property Group',
                quote: 'We manage 250 properties with a team that used to handle 80. The AI does what we used to pay 2 admin staff to do.',
                rating: 5
              },
              {
                name: 'Emma Thompson',
                role: 'Portfolio Manager',
                company: 'Brisbane Residential',
                quote: 'Finally, a platform built for Aussie property managers. The AI actually understands our workflows. Love it.',
                rating: 5
              }
            ].map((testimonial, index) => (
              <div key={index} className="p-8 rounded-2xl bg-white border border-gray-200 hover:shadow-xl transition-all">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-400 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                    <div className="text-xs text-gray-500">{testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6 bg-gradient-to-br from-violet-50 via-white to-cyan-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-violet-600 via-blue-600 to-cyan-600 bg-clip-text text-transparent mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Choose the plan that fits your portfolio size
            </p>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-100 to-cyan-100 text-violet-700 px-6 py-3 rounded-full font-medium shadow-lg">
              <Zap className="w-4 h-4" />
              <span>14-day free trial • No credit card required</span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: 'Free',
                price: '0',
                description: 'Perfect to get started',
                features: [
                  'Up to 25 properties',
                  'Basic AI maintenance triage',
                  'Unified inbox',
                  'Tenant & landlord portals',
                  'Email support',
                  'Community access'
                ],
                cta: 'Get Started Free',
                popular: false,
                gradient: 'from-gray-50 to-gray-100',
                accentColor: 'violet'
              },
              {
                name: 'Starter',
                price: '50',
                description: 'Most popular for growing agencies',
                features: [
                  'Up to 100 properties',
                  'Everything in Free',
                  'Advanced AI insights',
                  'Priority support',
                  'Mobile app access',
                  'Custom workflows',
                  'Analytics dashboard'
                ],
                cta: 'Start Free Trial',
                popular: true,
                gradient: 'from-violet-50 to-cyan-50',
                accentColor: 'violet'
              },
              {
                name: 'Professional',
                price: '100',
                description: 'For scaling operations',
                features: [
                  'Unlimited properties',
                  'Everything in Starter',
                  'White-label solution',
                  'API access',
                  '24/7 priority support',
                  'Custom integrations',
                  'Dedicated account manager'
                ],
                cta: 'Start Free Trial',
                popular: false,
                gradient: 'from-cyan-50 to-blue-50',
                accentColor: 'cyan'
              }
            ].map((plan, index) => (
              <div
                key={index}
                className={`relative p-8 rounded-3xl border-2 transition-all duration-300 hover:scale-105 ${plan.popular
                  ? 'bg-gradient-to-br from-violet-50 via-white to-cyan-50 border-violet-300 shadow-2xl ring-4 ring-violet-100'
                  : 'bg-white/80 backdrop-blur-sm border-gray-200 hover:border-violet-300 hover:shadow-xl'
                  }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-violet-600 to-cyan-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      ⭐ MOST POPULAR
                    </div>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600">{plan.description}</p>
                </div>

                <div className="text-center mb-8">
                  {plan.price === 'Custom' ? (
                    <div className="text-5xl font-bold bg-gradient-to-r from-violet-600 to-cyan-600 bg-clip-text text-transparent">
                      Custom
                    </div>
                  ) : (
                    <div>
                      <span className="text-6xl font-bold bg-gradient-to-r from-violet-600 to-cyan-600 bg-clip-text text-transparent">
                        ${plan.price}
                      </span>
                      <span className="text-gray-600 text-lg">/month</span>
                    </div>
                  )}
                </div>

                <Button
                  onClick={() => navigate('/login')}
                  className={`w-full py-6 text-lg mb-8 rounded-xl font-bold transition-all ${plan.popular
                    ? 'bg-gradient-to-r from-violet-600 via-purple-600 to-cyan-600 hover:from-violet-700 hover:to-cyan-700 text-white shadow-xl hover:shadow-2xl'
                    : 'bg-gradient-to-r from-gray-100 to-gray-200 hover:from-violet-50 hover:to-cyan-50 text-gray-900 border-2 border-gray-300 hover:border-violet-400'
                    }`}
                >
                  {plan.cta}
                </Button>

                <div className="space-y-4">
                  {plan.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r ${plan.popular ? 'from-violet-500 to-cyan-500' : 'from-green-400 to-emerald-500'
                        } flex items-center justify-center`}>
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-gray-700 leading-relaxed">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItaDJWMzRoLTJ6bTAtNGgydjJoLTJ2LTJ6bTAtNGgydjJoLTJ2LTJ6bTAtNGgydjJoLTJ2LTJ6bTAtNGgydjJoLTJ2LTJ6bTAtNGgydjJoLTJ2LTJ6bTAtNGgydjJoLTJ2LTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20" />
        <div className="max-w-4xl mx-auto text-center text-white relative z-10">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Ready to 10x Your Property Management?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join hundreds of Australian agencies saving 15+ hours per week with AI automation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button
              size="lg"
              onClick={() => navigate('/login')}
              className="bg-white text-blue-600 hover:bg-gray-100 px-10 py-7 text-lg shadow-2xl font-bold"
            >
              Start Your Free Trial
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-10 py-7 text-lg"
            >
              Book a Demo
            </Button>
          </div>
          <div className="flex items-center justify-center gap-8 text-sm opacity-75">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              <span>Setup in 5 minutes</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold">PropManager AI</span>
              </div>
              <p className="text-gray-400 leading-relaxed">
                The AI-native property management platform built for Australian agencies.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Product</h4>
              <ul className="space-y-3 text-gray-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Integrations</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Security</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Company</h4>
              <ul className="space-y-3 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Contact</h4>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  <span>hello@propmanager.ai</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  <span>1300 PROPMAN</span>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  <span>Sydney, Australia</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4 text-gray-400 text-sm">
            <p>© 2025 PropManager AI. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}